import { useMemo } from 'react';

import { PromotionContext } from './PromotionContext';
import { usePromotionState } from './usePromotionState';

import type { PromotionContextValue, PromotionProviderProps } from './types';

const noopSetPromotion = async () => {};
const noopClearPromotion = () => {};

export function PromotionProvider({
  children,
  defaultPromotion,
  enabled = true,
  loading = false,
}: PromotionProviderProps): React.JSX.Element {
  if (enabled) {
    return (
      <EnabledPromotionProvider defaultPromotion={defaultPromotion}>
        {children}
      </EnabledPromotionProvider>
    );
  }

  return (
    <DisabledPromotionProvider
      defaultPromotion={defaultPromotion}
      loading={loading}
    >
      {children}
    </DisabledPromotionProvider>
  );
}

interface PromotionProviderInnerProps {
  children: React.ReactNode;
  defaultPromotion: PromotionProviderProps['defaultPromotion'];
}

interface DisabledPromotionProviderProps extends PromotionProviderInnerProps {
  loading: boolean;
}

function DisabledPromotionProvider({
  children,
  defaultPromotion,
  loading,
}: DisabledPromotionProviderProps): React.JSX.Element {
  const value: PromotionContextValue = useMemo(
    () => ({
      couponCode: undefined,
      referrerType: undefined,
      referrerAssociatedAt: undefined,
      coupon: undefined,
      isValid: false,
      discountAmount: defaultPromotion.discountAmount,
      discountType: defaultPromotion.discountType,
      loading,
      error: undefined,
      isExpired: false,
      setPromotion: noopSetPromotion,
      clearPromotion: noopClearPromotion,
    }),
    [defaultPromotion, loading]
  );

  return (
    <PromotionContext.Provider value={value}>
      {children}
    </PromotionContext.Provider>
  );
}

function EnabledPromotionProvider({
  children,
  defaultPromotion,
}: PromotionProviderInnerProps): React.JSX.Element {
  const value = usePromotionState(defaultPromotion);

  return (
    <PromotionContext.Provider value={value}>
      {children}
    </PromotionContext.Provider>
  );
}
